import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { StonexMode, StonexSourceSystem, StonexTabId } from '../api/data';
import { MASTER_ACCOUNT } from './stonex-accounts';

export const STONEX_TABS: { id: StonexTabId; label: string }[] = [
  { id: 'liquidity', label: 'Liquidity Dashboard' },
  { id: 'positions', label: 'Positions' },
  { id: 'cash', label: 'Cash' },
  { id: 'balances', label: 'Balances' },
  { id: 'margins', label: 'Margins' },
  { id: 'pnl', label: 'P&L' },
  { id: 'trades', label: 'Trades' },
  { id: 'fees', label: 'Fees' },
  { id: 'prices', label: 'Prices' },
];

const VALID_TABS = new Set<StonexTabId>(STONEX_TABS.map((t) => t.id));
const VALID_MODES = new Set<StonexMode>(['eod', 'history']);
export const STONEX_SOURCE_SYSTEMS: StonexSourceSystem[] = ['GMI', 'Murex', 'XTP'];
const VALID_SOURCES = new Set<StonexSourceSystem>(STONEX_SOURCE_SYSTEMS);
// Defaults to the master so the dashboard opens on the consolidated view.
export const DEFAULT_ACCOUNT = MASTER_ACCOUNT;
const DEFAULT_TAB: StonexTabId = 'liquidity';
const DEFAULT_MODE: StonexMode = 'eod';
// XTP default: that's where the sub-account history lives (the launchpad's GMI default renders blank).
const DEFAULT_SOURCE: StonexSourceSystem = 'XTP';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isTab(value: string | null): value is StonexTabId {
  return value !== null && (VALID_TABS as Set<string>).has(value);
}
function isMode(value: string | null): value is StonexMode {
  return value !== null && (VALID_MODES as Set<string>).has(value);
}
export function isStonexSourceSystem(value: string | null): value is StonexSourceSystem {
  return value !== null && (VALID_SOURCES as Set<string>).has(value);
}
function isIsoDate(value: string | null | undefined): value is string {
  return typeof value === 'string' && ISO_DATE.test(value);
}

export interface StonexHistoryRange {
  start: string;
  end: string;
}

export interface StonexPageState {
  tab: StonexTabId;
  mode: StonexMode;
  accountNumber: string;
  historyRange: StonexHistoryRange;
  sourceSystem: StonexSourceSystem;
  // True when the source came from the URL — prices only honours explicit picks.
  sourceSystemExplicit: boolean;
}

function defaultHistoryRange(): StonexHistoryRange {
  const today = new Date();
  const start = new Date(today);
  start.setUTCDate(start.getUTCDate() - 7);
  return {
    start: start.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10),
  };
}

export function useStonexPageState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state: StonexPageState = useMemo(() => {
    const rawTab = searchParams.get('tab');
    const rawMode = searchParams.get('mode');
    const account = searchParams.get('account')?.trim();
    const rawFrom = searchParams.get('from');
    const rawTo = searchParams.get('to');
    const rawSource = searchParams.get('source');
    const defaults = defaultHistoryRange();
    return {
      tab: isTab(rawTab) ? rawTab : DEFAULT_TAB,
      mode: isMode(rawMode) ? rawMode : DEFAULT_MODE,
      accountNumber: account && account.length > 0 ? account : DEFAULT_ACCOUNT,
      historyRange: {
        start: isIsoDate(rawFrom) ? rawFrom : defaults.start,
        end: isIsoDate(rawTo) ? rawTo : defaults.end,
      },
      sourceSystem: isStonexSourceSystem(rawSource) ? rawSource : DEFAULT_SOURCE,
      sourceSystemExplicit: isStonexSourceSystem(rawSource),
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<StonexPageState>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          applyPatch(next, patch);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { state, update };
}

function applyPatch(next: URLSearchParams, patch: Partial<StonexPageState>): void {
  if (patch.tab !== undefined) applySingle(next, 'tab', patch.tab, DEFAULT_TAB);
  if (patch.mode !== undefined) applySingle(next, 'mode', patch.mode, DEFAULT_MODE);
  if (patch.accountNumber !== undefined) {
    applySingle(next, 'account', patch.accountNumber.trim(), DEFAULT_ACCOUNT);
  }
  if (patch.historyRange) {
    if (isIsoDate(patch.historyRange.start)) next.set('from', patch.historyRange.start);
    if (isIsoDate(patch.historyRange.end)) next.set('to', patch.historyRange.end);
  }
  if (patch.sourceSystem !== undefined) {
    // Always persisted so an explicit pick (even XTP) stays detectable.
    next.set('source', patch.sourceSystem);
  }
}

function applySingle(
  next: URLSearchParams,
  key: string,
  value: string,
  defaultValue: string,
): void {
  if (value === '' || value === defaultValue) next.delete(key);
  else next.set(key, value);
}
